import * as z from 'zod';

/**
 * Wire schemas for the LEGACY orders list (`GET /orders/for-merchant` on the
 * old backend). Transcribed from live responses, not from a spec — the old API
 * has no OpenAPI document, so the shapes here were captured off
 * `tback.noor.uz` and every optional/nullable marking reflects a value
 * actually observed on the wire.
 *
 * The schemas are deliberately LENIENT about unknown keys (Zod strips them by
 * default) and strict about the fields the table reads: the old backend adds
 * fields freely, and a legacy screen must not break because a new key
 * appeared. Fields that are genuinely absent for some rows — `courier` is null
 * until one is assigned — are modelled as nullable rather than optional so a
 * missing key still fails loudly.
 */

/** Courier summary embedded in a list row; null until one is assigned. */
export const legacyCourierSchema = z.object({
  id: z.number(),
  first_name: z.string(),
  last_name: z.string(),
  phone: z.string().nullable().default(null),
  /** Storage-relative path (not a URL) — resolve through the file API. */
  photo: z.string().nullable().default(null),
});

/**
 * The slice of a row's `origin[]` legs the table reads: the debt countdown
 * keys off `handed_over_at` and the live waiting fee off `arrived_at`.
 * Everything else on the leg belongs to the detail screen.
 */
export const legacyOrderRowLegSchema = z.object({
  arrived_at: z.string().nullable().default(null),
  handed_over_at: z.string().nullable().default(null),
});

/**
 * One row of the orders table. Only the fields the list actually renders are
 * modelled; the row payload carries much more (full `origin`/`destination`
 * leg objects with products and client data) that belongs to the order-detail
 * screen and is left unparsed here.
 */
export const legacyOrderSchema = z.object({
  /** Internal id — the one mutations address. */
  id: z.number(),
  /** The human-facing number shown in the ID column (`#27117399`). */
  display_id: z.number(),
  /** Merchant's own order reference; free text, often absent. */
  vendor_order_id: z.string().nullable().default(null),
  /** Backend stage 1–31 — drives the status badge. See `order-stages.ts`. */
  stage: z.number(),
  created_at: z.string(),
  total_delivery_price: z.number().default(0),
  /** Pickup / drop-off addresses. Multi-leg orders carry more than one entry. */
  origin_address: z.array(z.string()).default([]),
  destination_address: z.array(z.string()).default([]),
  courier: legacyCourierSchema.nullable().default(null),
  /** Whether the merchant's goods have been paid for by the courier. */
  product_paid: z.boolean().default(false),
  /** Courier payment state — see `product_paid_status` in the old app. */
  courier_payment_status: z.number().default(0),
  type: z.string().nullable().default(null),
  payment_type: z.string().nullable().default(null),
  /** Free waiting minutes before `price_per_minute_after_wait` starts. */
  free_wait_time: z.number().default(0),
  /** Nullable so the 500 so'm/min fallback can apply — see `extraWaitPrice`. */
  price_per_minute_after_wait: z.number().nullable().default(null),
  /** Goods total shown in the items column; `0` means "no products". */
  total_product_price: z.number().default(0),
  /** Accrued waiting fee shown in the waiting column. */
  wait_price: z.number().default(0),
  /** Tariff class — the live waiting fee only accrues for scooters (id 1). */
  equipment_id: z.number().nullable().default(null),
  /** Pickup legs, reduced to the two timestamps the table renders from. */
  origin: z.array(legacyOrderRowLegSchema).default([]),
  courier_paid_at: z.string().nullable().default(null),
  /** Storage-relative path of the courier's payment receipt photo. */
  courier_payment_receipt_url: z.string().nullable().default(null),
  /** Delivery terms; the payment column reads `product_paid` from here. */
  delivery: z
    .object({ product_paid: z.boolean().default(false) })
    .nullable()
    .default(null),
});

/**
 * Per-status row counts driving the filter pills. Every key is defaulted:
 * the backend omits a counter entirely when it is zero, and a pill showing
 * nothing is worse than one showing `(0)`.
 */
export const legacyOrderCountInfoSchema = z.object({
  total: z.number().default(0),
  active: z.number().default(0),
  in_search: z.number().default(0),
  cancelled: z.number().default(0),
  cancelled_with_payment: z.number().default(0),
  not_found: z.number().default(0),
  scheduled: z.number().default(0),
  finished: z.number().default(0),
  returned: z.number().default(0),
  not_paid_count: z.number().default(0),
  debt_count: z.number().default(0),
});

/**
 * The list envelope. Note this is the OLD backend's ad-hoc pagination object,
 * not the new gateway's `{data, meta}` — which is exactly why legacy mode has
 * its own HTTP client (see `legacyHttp`).
 */
export const legacyOrderListSchema = z.object({
  data: z.array(legacyOrderSchema).nullable().default([]),
  count: z.number().default(0),
  page_size: z.number().default(0),
  current_page: z.number().default(0),
  last_page: z.number().default(0),
  // Parsing an empty object applies every field's own default, so a response
  // with no `count_info` at all yields all-zero counters rather than
  // `undefined` — without restating the eleven zeroes here.
  count_info: legacyOrderCountInfoSchema.default(() =>
    legacyOrderCountInfoSchema.parse({}),
  ),
});

export type LegacyCourier = z.infer<typeof legacyCourierSchema>;
export type LegacyOrder = z.infer<typeof legacyOrderSchema>;
export type LegacyOrderCountInfo = z.infer<typeof legacyOrderCountInfoSchema>;
export type LegacyOrderList = z.infer<typeof legacyOrderListSchema>;
